import { Link, useLocation } from "wouter";
import { recordSearchClick } from "@/lib/searchTelemetry";
import { useState, useEffect, useCallback, useId } from "react";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Sparkles,
  Zap,
  Shield,
  Globe,
  ChevronDown,
  Unlock,
  ShieldCheck,
  Download,
  Languages,
  Code2,
  BookOpen,
  ScanLine,
  LayoutGrid,
  Hammer,
  FileUser,
  Newspaper,
  Shuffle,
  CalendarDays,
  Search,
  type LucideIcon,
} from "lucide-react";
import { visibleCategories, getToolById } from "@/lib/tools-data";
import { getCategoryName, getPersonaTitle, getPersonaTagline } from "@/lib/i18n/category-persona-translations";
import { useRecentTools } from "@/hooks/useFavorites";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { showUpdateBanner } from "@/hooks/useUpdateBanner";
import {
  updateSearchPanelQuery,
  beginSearchPanelSearch,
  finishSearchPanelSearch,
  failSearchPanelSearch,
  cancelSearchPanelSearch,
} from "@/hooks/useSearchPanel";
import { useIsMobile } from "@/hooks/useMobile";
import { toast } from "sonner";
import { type KnowledgeBaseSearchResults } from "@/lib/knowledgeBaseSearch";
import { SearchResultsDropdown } from "@/components/SearchResultsDropdown";
import { PurchaseConfirmation } from "@/components/PurchaseConfirmation";
import { FavoritesAndRecent } from "@/components/FavoritesAndRecent";
import latestBlogPosts from "@/lib/generated/blog-latest.json";
import { personas } from "@/lib/personas-data";
import { faqPreview } from "@/lib/faq-data";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useLocale } from "@/hooks/useLocale";
import { InstallSection } from "@/components/InstallSection";
import { LanguageTicker } from "@/components/LanguageTicker";
import { FEATURED_APP_META, FEATURED_APP_IDS } from "@/lib/featured-apps-data";
import { isPremiumApp } from "@/lib/tiers";

const FEATURED_ICONS = { BookOpen, ScanLine, LayoutGrid, FileUser, Newspaper, Shuffle, CalendarDays };
import { TransparentPricingBanner } from "@/components/TransparentPricingBanner";
import { TourVideo } from "@/components/home/TourVideo";
import { HeroSlides } from "@/components/home/HeroSlides";
import { ProofStrip, ProblemAndPromise, ApiMcpBand, CoverageClaim, PrivacyFlow, SupportRow } from "@/components/home/DeckSections";
import { SITE_FACTS, HOME_META_DESCRIPTION } from "@shared/siteFacts";
import { PREMIUM_APP_IDS } from "@shared/tiers";

// The three real offerings the site has grown into -- tools, workflow
// products, and the API/MCP layer -- each linking to its own existing
// page. No new pages, no routing changes: this only makes a structure
// that already exists easier to see from the homepage. English-only for
// now, same boundary as categoryDetails below and the tool content
// itself: not yet part of the translated i18n surface.
// Structural data only (icon + destination) -- stable across locales, so
// this stays a plain module-level const. The actual title/description/cta
// text is now pulled from strings.home.heroLane* inside HeroSection
// (translated into all 10 locales), not hardcoded here anymore.
// Structural data only -- stable across locales, so this stays a plain
// module-level const (same reasoning as before). Added since the last
// pass: count (each lane shows its OWN category number from SITE_FACTS, which a
// test checks against the tool registry and the generated catalogs), chips (short labels shown inside each
// card), and accent (which theme color token the card uses for its
// border/glow/numeral -- see --signal/--ember/--pulse in index.css).
//
// Chip content is deliberately untranslated in every language, on
// purpose, not by oversight:
//  - Tools: PDF/JPG/MP4 are file-format labels, the same everywhere a
//    format extension is ever written, in any language.
//  - Workflow: Book Writing Toolbox / Document Scanner are real product
//    names (matches PremiumAppsPreview's own actual listing).
//  - API: Postman / RapidAPI / MCP Registry are real third-party brand
//    names -- same policy already documented and in use for these exact
//    three in the now-removed-from-homepage PlatformsBanner component
//    (still live on /developers), not invented fresh here.
const heroLaneMeta: {
  id: "tools" | "premium" | "workflow" | "api" | "custom";
  icon: LucideIcon;
  href: string;
  count: string;
  chips: string[];
  accent: "signal" | "ember" | "pulse" | "bloom";
}[] = [
  { id: "tools", icon: LayoutGrid, href: "/professional-tools", count: SITE_FACTS.professionalToolCount, chips: ["PDF", "JPG", "MP4"], accent: "signal" },
  { id: "premium", icon: Sparkles, href: "/premium-apps", count: SITE_FACTS.premiumApplicationCount, chips: ["Invoices", "Forms", "Planners"], accent: "pulse" },
  { id: "workflow", icon: Shuffle, href: "/workflow-products", count: "", chips: ["Event Suite", "Book Writing", "Seller Studio", "Legal Desk"], accent: "ember" },
  { id: "api", icon: Code2, href: "/developers", count: SITE_FACTS.apiCount, chips: ["Postman", "RapidAPI", "MCP Registry"], accent: "pulse" },
  // No count: a custom build has no catalogue size to advertise. Chips state the
  // commercial model in three words each (see the page it links to).
  { id: "custom", icon: Hammer, href: "/custom-software", count: "", chips: ["Built for you", "One-time build", "Monthly usage"], accent: "bloom" },
];

function HeroSection() {
  const [, navigate] = useLocation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<KnowledgeBaseSearchResults>({ tools: [], faq: [], personas: [], blog: [], topResult: null });
  const [isFocused, setIsFocused] = useState(false);
  const isMobile = useIsMobile();
  const { strings } = useLocale();

  // Supports Google's Sitelinks Search Box (?q=... in the URL) and lets
  // people share/bookmark a pre-filled search link.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("q");
    if (q) setQuery(q);
  }, []);

  // Search runs only on submit (Enter), never while typing -- see the
  // matching comment in Layout.tsx's SearchBar for the two real,
  // confirmed problems this fixes (results overwriting what was being
  // typed, and acting on half-typed text).
  const [submitted, setSubmitted] = useState("");
  const [searching, setSearching] = useState(false);

  // As-you-type suggestions: only after 3+ characters and a 500 ms pause, tool names only (no AI call, no search log,
  // no analytics). The real search still runs only on Enter or the arrow.
  const listId = useId();
  const [suggestions, setSuggestions] = useState<{ id: string; title: string }[]>([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  useEffect(() => {
    const value = query.trim();
    setActiveIdx(-1);
    if (value.length < 3 || value === submitted) { setSuggestions([]); return; }
    let cancelled = false;
    const t = window.setTimeout(() => {
      import("@/lib/knowledgeBaseSearch")
        .then(({ suggestTools }) => {
          if (!cancelled) setSuggestions(suggestTools(value).map((d) => ({ id: d.id.replace(/^tool-/, ""), title: d.title })));
        })
        .catch(() => { /* suggestions are optional; Enter still searches */ });
    }, 500);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [query, submitted]);

  const submitSearch = useCallback((raw: string) => {
    const value = raw.trim();
    if (value.length < 2) return;
    const token = beginSearchPanelSearch(value);
    setSearching(true);
    import("@/lib/knowledgeBaseSearch")
      .then(async ({ searchKnowledgeBase }) => {
        const r = await searchKnowledgeBase(value);
        if (!finishSearchPanelSearch(token, value, r)) return; // superseded by a newer search
        setResults(r);
        setSubmitted(value);
        setSearching(false);
      })
      .catch(() => {
        failSearchPanelSearch(token);
        setSearching(false);
        showUpdateBanner();
      });
  }, []);

  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (value.trim().length < 2) {
      cancelSearchPanelSearch();
      setSearching(false);
      setSubmitted("");
      setResults({ tools: [], faq: [], personas: [], blog: [], topResult: null });
      updateSearchPanelQuery(value, { tools: [], faq: [], personas: [], blog: [], topResult: null });
    }
  };

  const goToTool = (toolId: string) => {
    navigate(`/tool/${toolId}`);
    setSuggestions([]);
    setQuery("");
    setResults({ tools: [], faq: [], personas: [], blog: [], topResult: null });
  };

  const onFieldKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => (i + 1) % suggestions.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1)); }
    else if (e.key === "Escape") { setSuggestions([]); setActiveIdx(-1); }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const value = query.trim();
    if (value.length < 2) return;
    // Enter on a highlighted suggestion opens that tool; otherwise Enter / the arrow runs the full search.
    if (activeIdx >= 0 && suggestions[activeIdx]) { goToTool(suggestions[activeIdx].id); return; }
    setSuggestions([]);
    // Results already shown for exactly this text: Enter opens the best
    // match (what Enter did before); otherwise Enter runs the search.
    if (value === submitted && !searching && results.topResult) {
      recordSearchClick(results.topResult.href, "enter", 0);
      navigate(results.topResult.href);
      setQuery("");
      setSubmitted("");
      setResults({ tools: [], faq: [], personas: [], blog: [], topResult: null });
      return;
    }
    submitSearch(value);
  };

  return (
    <section className="relative overflow-hidden">
      <div className="container py-10 md:py-14 grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-14 items-center">
        <div className="flex flex-col">
          <div className="max-w-xl mb-5 order-first lg:order-3">
            <form onSubmit={handleSearch} role="search">
              <div className="relative">
                <Sparkles className="absolute left-5 top-1/2 -translate-y-1/2 h-6 w-6 text-primary" />
                <input
                  type="text"
                  value={query}
                  onChange={e => handleQueryChange(e.target.value)}
                  onKeyDown={onFieldKeyDown}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setTimeout(() => setIsFocused(false), 200)}
                  placeholder={strings.nav.heroPlaceholder}
                  aria-label="Search tools"
                  role="combobox"
                  aria-expanded={isFocused && suggestions.length > 0}
                  aria-controls={listId}
                  aria-autocomplete="list"
                  aria-activedescendant={activeIdx >= 0 ? `${listId}-${activeIdx}` : undefined}
                  enterKeyHint="search"
                  inputMode="search"
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  className="w-full h-16 pl-14 pr-16 text-lg rounded-2xl border border-primary/50 bg-background shadow-sm hover:shadow-md focus:shadow-md focus:border-primary focus:outline-none transition-all"
                />
                <button
                  type="submit"
                  aria-label="Search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
                >
                  <ArrowRight className="h-5 w-5" />
                </button>
                {isFocused && suggestions.length > 0 && !searching && (
                  <ul
                    id={listId}
                    role="listbox"
                    aria-label="Suggested tools"
                    className="absolute left-0 right-0 top-full mt-2 z-50 overflow-hidden rounded-xl border bg-popover py-1 shadow-lg"
                  >
                    {suggestions.map((sg, i) => (
                      <li
                        key={sg.id}
                        id={`${listId}-${i}`}
                        role="option"
                        aria-selected={i === activeIdx}
                        onMouseDown={(e) => { e.preventDefault(); goToTool(sg.id); }}
                        onMouseEnter={() => setActiveIdx(i)}
                        className={`flex cursor-pointer items-center gap-3 px-4 py-2.5 text-sm ${i === activeIdx ? "bg-accent" : ""}`}
                      >
                        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="truncate">{sg.title}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {isMobile && isFocused && searching && (
                  <div className="absolute left-0 right-0 top-full mt-2 z-50 rounded-xl border bg-popover px-4 py-3 text-sm text-muted-foreground shadow-md">
                    Searching...
                  </div>
                )}
                {isMobile && isFocused && !searching && submitted.length > 1 && submitted === query.trim() && (
                  <SearchResultsDropdown
                    results={results}
                    query={query}
                    onSelectTool={goToTool}
                  />
                )}
              </div>
              <p className="mt-2 pl-1 text-xs text-muted-foreground">Press Enter or tap the arrow to search</p>
            </form>
          </div>

          <h1 className="lg:order-1 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4 md:mb-6 leading-[1.05]">
            {strings.home.heroTitleLine1}{" "}
            <br />
            <span className="gradient-text">{strings.home.heroTitleLine2}</span>
          </h1>

          <p className="lg:order-2 text-base md:text-lg text-muted-foreground max-w-xl mb-6 leading-relaxed">
            {strings.home.heroSubtitle}
          </p>

          <div className="lg:order-4 grid grid-cols-1 sm:grid-cols-2 gap-3.5 max-w-xl mb-6">
            {heroLaneMeta.map((meta, laneIndex) => {
              const lane =
                meta.id === "tools"
                  ? { ...meta, title: strings.home.heroLaneToolsTitle, description: strings.home.heroLaneToolsDesc, cta: strings.home.heroLaneToolsCta }
                  : meta.id === "premium"
                  ? { ...meta, title: strings.home.heroLanePremiumTitle, description: strings.home.heroLanePremiumDesc, cta: strings.home.heroLanePremiumCta }
                  : meta.id === "workflow"
                  ? { ...meta, title: strings.home.heroLaneWorkflowTitle, description: strings.home.heroLaneWorkflowDesc, cta: strings.home.heroLaneWorkflowCta }
                  : meta.id === "api"
                  ? { ...meta, title: strings.home.heroLaneApiTitle, description: strings.home.heroLaneApiDesc, cta: strings.home.heroLaneApiCta }
                  : { ...meta, title: strings.home.heroLaneCustomTitle, description: strings.home.heroLaneCustomDesc, cta: strings.home.heroLaneCustomCta };
              // --lane-accent feeds the laneGlow animation and every
              // color below -- one CSS custom property per card is what
              // lets three differently-colored cards share one keyframe
              // definition in index.css instead of needing three.
              const accentVar = `var(--${lane.accent})`;
              return (
              <Link
                key={lane.id}
                href={lane.href}
                className={`lane-card group relative flex flex-col rounded-2xl border p-4 bg-card overflow-hidden transition-transform hover:-translate-y-0.5 ${laneIndex === heroLaneMeta.length - 1 && heroLaneMeta.length % 2 === 1 ? "sm:col-span-2" : ""}`}
                style={{
                  ["--lane-accent" as string]: accentVar,
                  borderColor: `color-mix(in oklch, ${accentVar} 30%, var(--border))`,
                }}
              >
                {/* Soft color wash from the top-left corner -- deliberately
                    faint (8% at its own center, fading to nothing by
                    mid-card) so it reads as depth/tint rather than a loud
                    gradient wash across the whole card. */}
                <div
                  className="pointer-events-none absolute -top-8 -left-8 h-28 w-28 rounded-full opacity-[0.14] blur-2xl"
                  style={{ background: accentVar }}
                  aria-hidden="true"
                />

                <div className="relative flex items-start justify-between mb-2.5">
                  <span
                    className="flex h-9 w-9 items-center justify-center rounded-lg"
                    style={{ background: `color-mix(in oklch, ${accentVar} 16%, transparent)` }}
                  >
                    <lane.icon className="h-4.5 w-4.5" style={{ color: accentVar }} />
                  </span>
                  {lane.count && (
                    <span className="text-2xl font-extrabold tracking-tight" style={{ color: accentVar }}>
                      {lane.count}
                    </span>
                  )}
                </div>

                <span className="relative text-sm font-semibold mb-1">{lane.title}</span>
                <p className="relative text-xs text-muted-foreground leading-snug mb-2.5">
                  {lane.description}
                </p>

                <div className="relative flex flex-wrap gap-1.5 mb-3">
                  {lane.chips.map((chip) => (
                    <span
                      key={chip}
                      className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      style={{
                        background: `color-mix(in oklch, ${accentVar} 14%, transparent)`,
                        color: accentVar,
                      }}
                    >
                      {chip}
                    </span>
                  ))}
                </div>

                <span
                  className="relative text-xs font-semibold flex items-center gap-1 mt-auto"
                  style={{ color: accentVar }}
                >
                  {lane.cta}
                  <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
              );
            })}
          </div>

          <div className="lg:order-5 flex items-center gap-5 text-xs text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1.5">
              <Unlock className="h-3 w-3 text-primary" /> {strings.trustBadges.noLogin}
            </span>
            <span className="flex items-center gap-1.5">
              <Zap className="h-3 w-3 text-primary" /> {strings.trustBadges.freeUsesDaily}
            </span>
            <span className="flex items-center gap-1.5">
              <Shield className="h-3 w-3 text-primary" /> {strings.trustBadges.filesNeverStored}
            </span>
            <span className="flex items-center gap-1.5">
              <Globe className="h-3 w-3 text-primary" /> {strings.trustBadges.worksAnyDevice}
            </span>
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="h-3 w-3 text-primary" /> {strings.trustBadges.worksWhenAiBlocked}
            </span>
            <span className="flex items-center gap-1.5">
              <Languages className="h-3 w-3 text-primary" /> {strings.trustBadges.availableInLanguages}
            </span>
          </div>
        </div>

        <HeroSlides />
      </div>

      <div className="text-center pb-4 animate-bounce">
        <ChevronDown className="h-5 w-5 mx-auto text-muted-foreground/60" />
      </div>
    </section>
  );
}

// Extended descriptions for each category. Every tool name is represented
// somewhere below -- related tools are combined onto one line to keep card
// heights consistent, but nothing is silently omitted.
const categoryDetails: Record<string, string[]> = {
  "pdf-tools": [
    "Merge & split PDFs",
    "Compress (Balanced, Aggressive, Extreme or file size) & rotate PDFs",
    "PDF to Word, JPG & Excel",
    "Word & Image to PDF",
    "Add/remove passwords",
    "Add watermarks",
    "Extract pages, images & text",
    "PDF editor",
  ],
  "powerpoint-slides": [
    "PowerPoint to PDF",
    "PowerPoint to JPG/PNG pictures",
    "Extract pictures, text & notes",
    "Clean a deck before sharing",
    "Images, Markdown & PDF to PowerPoint",
    "Split, merge, compress & reorder decks",
    "Find & replace text and fonts, number and watermark slides",
    "Resize slides, check accessibility, extract chart data",
    "Word, Excel, slide deck maker & photo slideshows",
    "Repair, redact and password-protect a deck",
    "PowerPoint to video, GIF & HTML, templates & Presentation Studio",
  ],
  "image-tools": [
    "Compress images",
    "Resize & crop",
    "Convert formats (PNG, JPG, WebP, AVIF, TIFF & more)",
    "Background removal",
    "Flip, rotate & filters",
    "Image to PDF",
    "Bulk processor",
    "Passport & visa photos",
    "Screenshot tool",
    "Camera capture",
    "Document scanner",
    "Photo collage maker",
  ],
  "video-tools": [
    "Compress video files",
    "Convert formats",
    "Trim & cut clips",
    "Merge videos",
    "Video to GIF",
    "Generate thumbnails",
    "Add subtitles",
    "Screen recorder",
  ],
  "audio-tools": [
    "Extract audio from video",
    "Trim & merge audio",
    "Convert formats (MP3, WAV, OGG, M4A, FLAC)",
    "Compress audio files",
    "Voice recorder",
    "Speed changer (pitch-preserving)",
    "Silence remover",
    "Volume normalizer",
    "Compare two audio files",
    "Background noise remover",
    "Voice disguiser",
  ],
  "text-tools": [
    "Word & character counter",
    "Case converter",
    "Paraphrasing & rewriting",
    "Grammar checker",
    "Lorem ipsum generator",
    "Find & replace, text reverser",
    "Text-to-speech",
    "Password generator",
    "Fancy font & invisible text generators",
    "Text sorter, email & URL extractors",
    "Slug generator & reading level checker",
    "ASCII art & Markdown/HTML converter",
  ],
  converters: [
    "Unit converter",
    "Currency converter",
    "Time zone converter",
    "Tip calculator",
    "Age & date calculator",
    "Recipe scaler",
    "Percentage & GPA calculators",
    "Roman numeral & random number tools",
  ],
  "tax-calculators": [
    "Sales tax calculator",
    "VAT calculator",
    "GST calculator",
    "Self-employment tax",
    "State tax estimator",
    "Income tax calculator",
    "India income tax (old vs new)",
    "UK tax & National Insurance",
  ],
  "finance-calculators": [
    "Loan/EMI calculator with first payment date",
    "Mortgage calculator with first payment date",
    "Paycheck estimator",
    "Discount & compound interest",
    "SIP investment calculator",
    "Social Security estimator",
    "401(k) projector",
    "RMD & FIRE calculators",
  ],
  "health-calculators": [
    "BMI & calorie calculators",
    "Body fat percentage",
    "BMR & macro calculator",
    "Ideal weight calculator",
    "Pregnancy due date",
    "Daily water intake",
  ],
  "qr-tools": [
    "QR code generator",
    "Custom QR code styling",
    "Bulk QR generator",
    "QR & barcode scanner",
    "Barcode generator",
  ],
  "business-tools": [
    "Resume/CV builder",
    "Invoice & receipt generators",
    "Business card designer",
    "Contract templates",
    "Expense tracker",
    "Official government forms (W-4, W-9, I-9)",
    "NDA & lease agreement templates",
    "Power of attorney, bill of sale & promissory note",
    "Eviction notice & startup name generator",
    "Product description & user manual writers",
    "Invention disclosure worksheet",
    "Purchase orders, quotes, packing slips & label sheets",
    "Blind CV redactor, resume keyword matcher & bank statement to CSV",
  ],
  "design-tools": [
    "Color palette generator",
    "HEX/RGB/HSL converter",
    "Gradient generator",
    "Favicon generator",
    "Logo maker",
    "Mockup generator",
    "Image to SVG",
    "Color picker from image",
    "Social media asset formatter",
    "Box shadow & border radius generators",
    "Contrast checker & font pairing tool",
  ],
  "security-tools": [
    "Password generator & strength checker",
    "Base64 encode/decode",
    "JSON formatter",
    "Regex tester",
    "URL encoder/decoder",
    "Hash generator (MD5/SHA)",
    "JWT decoder",
    "UUID generator",
    "Diff checker",
    "Binary/hex/ASCII & timestamp converters",
    "Cron builder & HTTP status reference",
    "HTML entity converter, CSS/JS minifier & user-agent parser",
  ],
  "seo-tools": [
    "Meta tag generator",
    "Keyword density checker",
    "Hashtag generator",
    "Social caption generator",
    "Robots.txt & sitemap builder",
    "OG/Twitter card preview",
    "Schema markup (JSON-LD) generator",
  ],
  "file-tools": [
    "ZIP/UnZIP in browser",
    "Audio format converter",
    "Excel/CSV converter",
    "Meme generator",
    "OCR text extraction",
    "File size calculator",
    "YouTube thumbnail downloader",
  ],
  "3d-tools": [
    "3D mesh converter (STL/OBJ/PLY)",
    "STL ASCII/binary converter",
    "3D mesh viewer with wireframe preview",
  ],
  "ecommerce-tools": [
    "Profit margin calculator",
    "Marketplace fee calculator",
    "Shipping cost estimator",
    "Break-even calculator",
  ],
  "travel-tools": [
    "Travel budget planner",
    "Itinerary planner",
    "Packing list generator",
    "Countdown & to-do planner",
  ],
  "event-tools": [
    "Event budget planner",
    "Checklist & timeline planner",
    "Guest list & RSVP tracker",
    "Day-of schedule builder",
    "Name badges, passes & certificates",
    "Booth layouts, seating charts & rooming lists",
    "Invitations, calendar invites & event landing pages",
    "Vendor quotes, expense capture & event reports",
  ],
  "animation-tools": [
    "Emoji picker & copy",
    "Emoji to Unicode/HTML converter",
    "Avatar builder (no photo needed)",
    "Sprite sheet generator & splitter",
  ],
  "household-tools": [
    "Monthly budget planner",
    "Grocery list planner",
    "Shopping list planner",
    "Home inventory tracker",
  ],
  "college-tools": [
    "College cost calculator",
    "Student loan repayment calculator",
    "529 savings calculator",
    "Citation generator (APA/MLA/Chicago/Harvard)",
    "Text similarity checker",
    "Paper format checker",
  ],
  "relocation-tools": [
    "Moving cost estimator",
    "Relocation checklist & timeline",
    "Cost of living comparison",
  ],
  "marketing-design-tools": [
    "Menu card maker",
    "Invitation card maker",
    "Brochure & flyer maker",
    "Banner & sign maker",
  ],
};

// Explicit bento placement (6-col grid). First category is the featured
// XL tile, next two are tall L tiles, remaining four are compact M tiles,
// followed by the "view all" tile. Placement is positional, not tied to
// specific category IDs, so this stays correct if category order changes.
// Every category on one page, compact: the whole map of the site at a glance.
const CHIPS_PER_CARD = 4;

function CategoryGrid() {
  const { strings, localizedHref, locale } = useLocale();
  const featured = visibleCategories;

  return (
    <section className="container py-16">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight mb-1 gradient-text">
            {strings.home.exploreToolkitTitle}
          </h2>
          <p className="text-sm text-muted-foreground">
            {strings.home.exploreToolkitSubtitle.replace("{catCount}", String(visibleCategories.length))}
          </p>
        </div>
        <Link
          href={localizedHref("/tools")}
          className="text-xs font-mono text-muted-foreground hover:text-primary transition-colors shrink-0"
        >
          {strings.home.categoriesLabel.replace("{count}", String(visibleCategories.length))}
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {featured.map(category => {
          const tools = category.tools.filter(t => !t.hidden && !FEATURED_APP_IDS.has(t.id));
          const shown = tools.slice(0, CHIPS_PER_CARD);
          const extra = tools.length - shown.length;

          return (
            <div
              key={category.id}
              className="card-raised p-4 rounded-2xl flex flex-col"
            >
              <Link href={localizedHref(`/category/${category.id}`)} className="flex items-center gap-3 mb-4 group">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${category.color}15` }}
                >
                  <category.icon className="h-5 w-5" style={{ color: category.color }} />
                </div>
                <div>
                  <h3 className="font-semibold text-sm card-heading">{getCategoryName(locale, category.id, category.name)}</h3>
                  <span className="text-xs font-mono text-muted-foreground">{tools.length} TOOLS</span>
                </div>
              </Link>

              <div className="flex flex-wrap gap-2">
                {shown.map(tool => (
                  <Link key={tool.id} href={localizedHref(`/tool/${tool.id}`)} className="tool-chip">
                    {tool.name}
                  </Link>
                ))}
                {extra > 0 && (
                  <Link href={localizedHref(`/category/${category.id}`)} className="tool-chip tool-chip-more">
                    +{extra} more
                  </Link>
                )}
              </div>
            </div>
          );
        })}

      </div>
    </section>
  );
}

function CorporateSafePreview() {
  const { strings, localizedHref } = useLocale();
  return (
    <section className="container py-10 border-t border-border/40">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold tracking-tight mb-1 gradient-text">
          {strings.home.corporateSafeTitle}
        </h2>
        <p className="text-muted-foreground text-sm">
          {strings.home.corporateSafeSubtitle}
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="p-4 rounded-xl border border-border/60 bg-card">
          <h3 className="font-semibold text-sm mb-1 card-heading">{strings.home.corporateSafeBlockedTitle}</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {strings.home.corporateSafeBlockedDesc}
          </p>
        </div>
        <div className="p-4 rounded-xl border border-primary/40 bg-primary/5">
          <h3 className="font-semibold text-sm mb-1 card-heading">{strings.home.corporateSafeGovernedTitle}</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {strings.home.corporateSafeGovernedDesc}
          </p>
        </div>
        <div className="p-4 rounded-xl border border-border/60 bg-card">
          <h3 className="font-semibold text-sm mb-1 card-heading">{strings.home.corporateSafeOpenTitle}</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {strings.home.corporateSafeOpenDesc}
          </p>
        </div>
      </div>
      <div className="text-center mt-5">
        <Link
          href={localizedHref("/corporate-safe-tools")}
          className="text-sm font-medium text-primary hover:underline"
        >
          {strings.home.corporateSafeCta}
        </Link>
      </div>
    </section>
  );
}

function ForYourWorkPreview() {
  const { strings, locale, localizedHref } = useLocale();
  // A curated, fixed subset for the homepage -- the full set of 13 lives
  // at /solutions (and in the "For Your Work" nav dropdown already).
  // Showing all 13 here would compete for space with the category grid;
  // this is a preview with a clear path to the rest.
  const featured = personas.slice(0, 9);

  return (
    <section className="container py-16 border-t border-border/40">
      <div className="text-center mb-10">
        <h2 className="text-2xl font-bold tracking-tight mb-2 gradient-text">
          {strings.home.forYourWorkTitle}
        </h2>
        <p className="text-muted-foreground text-sm">
          {strings.home.forYourWorkSubtitle}
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {featured.map(persona => (
          <Link key={persona.id} href={localizedHref(`/solutions/${persona.id}`)}>
            <div className="p-5 rounded-xl border border-primary/50 bg-card hover:border-primary transition-colors cursor-pointer h-full flex flex-col group">
              <div
                className="w-11 h-11 rounded-lg flex items-center justify-center mb-3 transition-transform group-hover:scale-110"
                style={{ backgroundColor: `${persona.color}15` }}
              >
                <persona.icon className="h-5 w-5" style={{ color: persona.color }} />
              </div>
              <h3 className="font-semibold text-sm mb-1.5 card-heading">{getPersonaTitle(locale, persona.id, persona.title)}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed flex-1">
                {getPersonaTagline(locale, persona.id, persona.tagline)}
              </p>
              <div className="mt-3 flex items-center text-xs text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                {strings.home.exploreTools} <ArrowRight className="ml-1 h-3 w-3" />
              </div>
            </div>
          </Link>
        ))}
      </div>
      <div className="text-center mt-8">
        <Link href={localizedHref("/solutions")} className="text-sm font-medium text-primary hover:underline">
          {strings.home.seeAllRoles.replace("{count}", String(personas.length))}
        </Link>
      </div>
    </section>
  );
}

// The monthly workflow products: whole jobs from start to finish, each with a 7-day trial.
const WORKFLOW_HOME_IDS: { id: string; icon: LucideIcon; accent: string }[] = [
  { id: "event-suite", icon: CalendarDays, accent: "signal" },
  { id: "book-writing-toolbox", icon: BookOpen, accent: "ember" },
  { id: "seller-studio", icon: LayoutGrid, accent: "primary" },
  { id: "legal-production-desk", icon: Shuffle, accent: "ember" },
];

function WorkflowProductsPreview() {
  const { strings, localizedHref } = useLocale();
  return (
    <section className="container py-16 border-t border-border/40">
      <div className="text-center mb-10">
        <h2 className="text-2xl font-bold tracking-tight mb-2 gradient-text">{strings.home.heroLaneWorkflowTitle}</h2>
        <p className="text-muted-foreground text-sm max-w-2xl mx-auto">{strings.home.heroLaneWorkflowDesc}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {WORKFLOW_HOME_IDS.map(({ id, icon: Icon, accent }) => {
          const tool = getToolById(id);
          if (!tool) return null;
          return (
            <Link key={id} href={localizedHref(`/tool/${id}`)}>
              <div className="p-5 rounded-xl border border-primary/50 bg-card hover:border-primary transition-colors cursor-pointer h-full flex flex-col group">
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center mb-3 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `var(--${accent})`, color: accent === "signal" ? "var(--signal-foreground)" : "#FFFFFF" }}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-semibold text-sm mb-1.5 card-heading">{tool.name}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed flex-1">{tool.description}</p>
              </div>
            </Link>
          );
        })}
      </div>
      <div className="text-center mt-8">
        <Link href={localizedHref("/workflow-products")} className="text-sm font-medium text-primary hover:underline">
          {strings.home.heroLaneWorkflowCta} →
        </Link>
      </div>
    </section>
  );
}

function PremiumAppsPreview() {
  const { strings, localizedHref } = useLocale();

  return (
    <section className="container py-16 border-t border-border/40">
      <div className="text-center mb-10">
        <h2 className="text-2xl font-bold tracking-tight mb-2 gradient-text">
          {strings.premiumApps.title}
        </h2>
        <p className="text-muted-foreground text-sm">
          {strings.premiumApps.subtitle}
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {FEATURED_APP_META.filter((a) => isPremiumApp(a.id)).map((app) => {
          const Icon = FEATURED_ICONS[app.iconName];
          return (
            <Link key={app.id} href={localizedHref(`/tool/${app.id}`)}>
              <div className="p-5 rounded-xl border border-primary/50 bg-card hover:border-primary transition-colors cursor-pointer h-full flex flex-col group">
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center mb-3 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `var(--${app.accent})`, color: app.accent === "signal" ? "var(--signal-foreground)" : "#FFFFFF" }}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-semibold text-sm mb-1.5 card-heading">{app.name}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed flex-1">{app.description}</p>
              </div>
            </Link>
          );
        })}
      </div>
      <div className="text-center mt-8">
        <Link href={localizedHref("/premium-apps")} className="text-sm font-medium text-primary hover:underline">
          {strings.premiumApps.seeAll}
        </Link>
      </div>
    </section>
  );
}

function FaqPreview() {
  const { strings, localizedHref } = useLocale();
  return (
    <section className="container py-16 border-t border-border/40">
      <div className="text-center mb-10">
        <h2 className="text-2xl font-bold tracking-tight mb-2 gradient-text">
          {strings.home.faqTitle}
        </h2>
      </div>
      <div className="max-w-2xl mx-auto">
        <Accordion type="single" collapsible className="w-full space-y-3">
          {faqPreview.map((item, i) => (
            <AccordionItem key={i} value={`preview-${i}`} className="border border-primary/50 rounded-xl px-4">
              <AccordionTrigger className="text-left">
                {item.question}
              </AccordionTrigger>
              <AccordionContent>{item.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="text-center mt-6">
          <Link href={localizedHref("/faq")} className="text-sm font-medium text-primary hover:underline">
            {strings.home.faqMoreLink}
          </Link>
        </div>
      </div>
    </section>
  );
}

function ToolkitProse() {
  const { strings } = useLocale();
  return (
    <section className="container py-16 border-t border-border/40">
      <h2 className="text-2xl font-bold tracking-tight mb-6 gradient-text">
        {strings.home.toolkitProseTitle}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 text-sm text-muted-foreground leading-relaxed">
        <div className="space-y-4">
          <p>{strings.home.toolkitProsePara1}</p>
        </div>
        <div className="space-y-4">
          <p>{strings.home.toolkitProsePara4}</p>
        </div>
      </div>
    </section>
  );
}

function ClosingCta() {
  const { canPrompt, isInstalled, isIOS, promptInstall } = useInstallPrompt();
  const { strings } = useLocale();

  // Telling someone who's already installed the app to "add the
  // shortcut" is just noise -- and if this browser can't prompt at all
  // (and isn't iOS, which gets its own instructions instead), there's
  // nothing for a click here to actually do. Same visibility rule the
  // header's own Create Shortcut button already follows.
  if (isInstalled || (!canPrompt && !isIOS)) return null;

  const handleClick = async () => {
    if (isIOS) {
      toast.info(strings.install.addToHomeScreenTitle, {
        description: strings.install.iosInstructions,
        duration: 9000,
      });
      return;
    }
    const outcome = await promptInstall();
    if (outcome === "accepted") {
      toast.success(strings.install.desktopSuccess);
    }
  };

  return (
    <section className="container py-14 border-t border-border/40">
      <button
        onClick={handleClick}
        className="w-full max-w-2xl mx-auto rounded-2xl border border-primary/30 bg-primary/5 p-8 flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left hover:border-primary/50 hover:bg-primary/10 transition-colors cursor-pointer"
      >
        <div className="w-12 h-12 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
          <Download className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-semibold text-base mb-1.5">
            {strings.home.closingCtaTitle}
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {strings.home.closingCtaDesc}
          </p>
        </div>
      </button>
    </section>
  );
}

function BlogPreview() {
  const { strings, localizedHref } = useLocale();
  const latestPosts = latestBlogPosts.slice(0, 2);

  return (
    <section className="container py-12">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold tracking-tight gradient-text">{strings.home.blogTitle}</h2>
        <Link
          href={localizedHref("/blog")}
          className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
        >
          {strings.home.viewAllPosts} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        {latestPosts.map(post => (
          <Link key={post.slug} href={localizedHref(`/blog/${post.slug}`)}>
            <article className="p-5 rounded-xl border border-primary/50 bg-card hover:border-primary transition-colors cursor-pointer h-full">
              <h3 className="font-semibold text-base mb-2 card-heading">{post.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {post.description}
              </p>
            </article>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const { locale, pathInLocale } = useLocale();
  usePageMeta({
    title: `Every Professional Task, One Browser Platform: ${SITE_FACTS.toolCountLabel} Tools`,
    description: HOME_META_DESCRIPTION,
    path: pathInLocale(locale),
    localized: true,
  });

  return (
    <div className="page-enter">
      <PurchaseConfirmation />
      <HeroSection />
      <TourVideo />
      <ProofStrip />
      <ProblemAndPromise />
      <LanguageTicker />
      <FavoritesAndRecent />
      <CategoryGrid />
      {/* Moved up from after PremiumAppsPreview (was 11th of 15 sections,
          effectively invisible to anyone who didn't scroll past Hero,
          Install prompts, Favorites, and the category grid). Placed right
          after someone has actually seen what's on offer, but before the
          rest of the exploratory content — Corporate-Safe, For Your
          Work, Premium Applications -- rather than after all of it. */}
      <CoverageClaim />
      <TransparentPricingBanner />
      <PrivacyFlow />
      <CorporateSafePreview />
      <ForYourWorkPreview />
      <WorkflowProductsPreview />
      <PremiumAppsPreview />
      <BlogPreview />
      <ApiMcpBand />
      <SupportRow />
      <FaqPreview />
      <ToolkitProse />
      <InstallSection />
    </div>
  );
}
